"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { StudioApiError, studioApi, studioErrorCopy } from "./lib/api";
import { appointmentRangeAroundToday, istanbulHour } from "./lib/date";
import type {
  Appointment,
  AppointmentStatus,
  CancellationMode,
  Customer,
  CustomerAppCredential,
  CostEntry,
  LedgerEntry,
  MobileActivity,
  PackageRules,
  PackageRecord,
  PackagePlan,
  Payment,
  ReversalLog,
  Opportunity,
  GoalDailyEntry,
  StaffGoal,
  StaffMember,
  StudioClosureLog,
  TeamNote,
  TrainerException,
  TrainerSchedule,
  WorkPriority,
  WorkTask,
  WorkspaceSettings,
  Workshop,
  WorkshopInput,
  WorkshopRegistration
} from "./types";

type ConnectionState = "loading" | "live" | "error";
type OperationPhase = "working" | "success" | "error";

export interface OperationState {
  phase: OperationPhase;
  message: string;
}

interface AppointmentRange {
  from: string;
  to: string;
}

interface StudioData {
  customers: Customer[];
  setCustomers: React.Dispatch<React.SetStateAction<Customer[]>>;
  appointments: Appointment[];
  setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>;
  mobileActivities: MobileActivity[];
  records: PackageRecord[];
  packagePlans: PackagePlan[];
  paymentMethods: Array<{ code: string; name: string }>;
  serviceTypes: Array<{ code: string; name: string; capacity: number }>;
  payments: Payment[];
  expenses: CostEntry[];
  opportunities: Opportunity[];
  ledger: LedgerEntry[];
  setLedger: React.Dispatch<React.SetStateAction<LedgerEntry[]>>;
  packageRules: PackageRules;
  setPackageRules: React.Dispatch<React.SetStateAction<PackageRules>>;
  workspaceSettings: WorkspaceSettings;
  staff: StaffMember[];
  staffSchedules: TrainerSchedule[];
  scheduleExceptions: TrainerException[];
  teamNotes: TeamNote[];
  workTasks: WorkTask[];
  staffGoals: StaffGoal[];
  goalDailyEntries: GoalDailyEntry[];
  reversalLogs: ReversalLog[];
  studioClosureLogs: StudioClosureLog[];
  workshops: Workshop[];
  workshopManagementReady: boolean;
  connectionState: ConnectionState;
  connectionError: string | null;
  lastSyncedAt: string | null;
  appointmentRange: AppointmentRange;
  appointmentsLoading: boolean;
  operationState: OperationState | null;
  refresh: () => Promise<void>;
  loadAppointmentRange: (range: AppointmentRange) => Promise<void>;
  createCustomer: (customer: Customer) => Promise<{
    customerId: string;
    mobileCredential?: CustomerAppCredential;
    mobileAccountError?: string;
  }>;
  updateCustomer: (customer: Customer) => Promise<void>;
  createAppointments: (payload: {
    customerId: string;
    recordId: string;
    date: string;
    time: string;
    trainer: string;
    note?: string;
    repeatCount?: number;
  }) => Promise<void>;
  setAppointmentStatus: (
    appointment: Appointment,
    status: AppointmentStatus,
    sessionNotes?: string,
    completedBy?: string,
    cancellationMode?: CancellationMode,
    cancellationReason?: string,
    extendExpiryDays?: number
  ) => Promise<void>;
  closeStudioDay: (payload: { date: string; reason?: string }) => Promise<void>;
  extendStudioClosurePackages: (payload: { date: string; days: number }) => Promise<void>;
  voidAppointment: (appointment: Appointment, removalNote?: string) => Promise<void>;
  createHold: (payload: {
    customerId: string;
    date: string;
    time: string;
    trainer?: string;
    note?: string;
  }) => Promise<void>;
  confirmHold: (
    hold: Appointment,
    packageId: string,
    completeNow: boolean,
    sessionNotes?: string,
    completedBy?: string
  ) => Promise<void>;
  cancelHold: (hold: Appointment) => Promise<void>;
  deleteHold: (hold: Appointment) => Promise<void>;
  addRecord: (record: PackageRecord) => Promise<void>;
  updateRecord: (record: PackageRecord) => Promise<void>;
  addPayment: (payment: Payment) => Promise<void>;
  removePayment: (paymentId: string) => Promise<void>;
  createExpense: (payload: { category: string; description: string; amount: number; date: string; note?: string }) => Promise<void>;
  voidExpense: (expense: CostEntry) => Promise<void>;
  createOpportunity: (opportunity: Opportunity) => Promise<void>;
  updateOpportunity: (opportunity: Opportunity) => Promise<void>;
  archiveOpportunity: (opportunity: Opportunity) => Promise<void>;
  adjustCancellationUsage: (recordId: string, difference: number) => void;
  burnRecordRights: (recordId: string) => Promise<void>;
  updatePackagePlanPrices: (updates: Array<{ id: string; version: string; defaultPrice: number }>, financePin: string) => Promise<void>;
  savePackageRules: (rules: PackageRules) => Promise<void>;
  saveWorkspaceSettings: (settings: WorkspaceSettings, rules: PackageRules) => Promise<void>;
  createTeamNote: (payload: {
    body: string;
    priority: WorkPriority;
    dueOn?: string;
    assignedToId?: string;
    authorStaffId?: string;
  }) => Promise<void>;
  setTeamNoteStatus: (note: TeamNote, status: "OPEN" | "DONE" | "ARCHIVED") => Promise<void>;
  createWorkTask: (payload: {
    title: string;
    description?: string;
    priority: WorkPriority;
    recurrence: WorkTask["recurrence"];
    dueOn: string;
    assignedToId: string;
    createdByStaffId?: string;
  }) => Promise<void>;
  setWorkTaskStatus: (task: WorkTask, status: WorkTask["status"] | "CANCELLED") => Promise<void>;
  createStaffGoal: (payload: {
    title: string;
    metricLabel: string;
    metricCode: StaffGoal["metricCode"];
    ownerId: string;
    periodStart: string;
    periodEnd: string;
    priority: WorkPriority;
    startValue: number;
    currentValue: number;
    targetValue: number;
    rewardAmount: number;
    aggregation: StaffGoal["aggregation"];
    notes?: string;
  }) => Promise<void>;
  updateStaffGoalProgress: (goal: StaffGoal, currentValue: number) => Promise<void>;
  addGoalDailyResult: (payload: {
    goalId: string;
    entryDate: string;
    value: number;
    recordedByStaffId?: string;
    note?: string;
  }) => Promise<void>;
  createScheduleExceptions: (payload: {
    staffId: string;
    dates: string[];
    startTime: string;
    endTime: string;
    allDay: boolean;
    note?: string;
  }) => Promise<void>;
  cancelScheduleException: (exception: TrainerException) => Promise<void>;
  createWorkshop: (workshop: WorkshopInput) => Promise<void>;
  updateWorkshop: (workshop: Workshop, input: WorkshopInput) => Promise<void>;
  archiveWorkshop: (workshop: Workshop) => Promise<void>;
  cancelWorkshopParticipation: (
    registration: WorkshopRegistration,
    reason: string
  ) => Promise<void>;
}

const StudioContext = createContext<StudioData | null>(null);

type StudioCommand = Parameters<typeof studioApi.command>[0];

const operationCopy: Partial<Record<StudioCommand, { working: string; success: string }>> = {
  CREATE_CUSTOMER: { working: "Müşteri kaydı oluşturuluyor…", success: "Müşteri kaydı oluşturuldu." },
  UPDATE_CUSTOMER: { working: "Müşteri bilgileri güncelleniyor…", success: "Müşteri bilgileri güncellendi." },
  CREATE_MOBILE_ACCOUNT: { working: "Mobil uygulama erişimi hazırlanıyor…", success: "Mobil uygulama erişimi hazırlandı." },
  RESET_MOBILE_PASSWORD: { working: "Geçici şifre yenileniyor…", success: "Yeni geçici şifre hazırlandı." },
  CREATE_WORKSHOP: { working: "Atölye programı oluşturuluyor…", success: "Atölye programa eklendi." },
  UPDATE_WORKSHOP: { working: "Atölye bilgileri güncelleniyor…", success: "Atölye bilgileri güncellendi." },
  ARCHIVE_WORKSHOP: { working: "Atölye yayından kaldırılıyor…", success: "Atölye yayından kaldırıldı." },
  CANCEL_WORKSHOP_PARTICIPATION: { working: "Atölye katılımı iptal ediliyor…", success: "Katılım iptal edildi ve paket hakkı iade edildi." },
  CREATE_APPOINTMENTS: { working: "Seans programı oluşturuluyor…", success: "Seans programı oluşturuldu." },
  SET_APPOINTMENT_STATUS: { working: "Seans durumu güncelleniyor…", success: "Seans durumu güncellendi." },
  CANCEL_APPOINTMENT_PROTECTED: { working: "Seans hakkı korunarak iptal ediliyor…", success: "Randevu iptal edildi; müşterinin hakları korundu." },
  CLOSE_STUDIO_DAY: { working: "Seçilen günün programı kapatılıyor…", success: "Günün planlı randevuları haklar korunarak iptal edildi." },
  EXTEND_STUDIO_CLOSURE_PACKAGES: { working: "Paket süreleri uzatılıyor…", success: "Kapanan güne bağlı paket süreleri uzatıldı." },
  REOPEN_APPOINTMENT: { working: "Seans işlemleri geri alınıyor…", success: "Seans yeniden planlandı." },
  VOID_APPOINTMENT: { working: "Seans kaydı kaldırılıyor…", success: "Seans kaydı kaldırıldı." },
  CREATE_HOLD: { working: "Takvime bloke ekleniyor…", success: "Bloke takvime eklendi." },
  CONFIRM_HOLD: { working: "Bloke seansa dönüştürülüyor…", success: "Bloke seansa dönüştürüldü." },
  CANCEL_HOLD: { working: "Bloke kaldırılıyor…", success: "Bloke kaldırıldı." },
  DELETE_HOLD: { working: "Bloke tamamen siliniyor…", success: "Bloke tamamen silindi." },
  CREATE_PACKAGE: { working: "Paket kaydı oluşturuluyor…", success: "Paket kaydı oluşturuldu." },
  UPDATE_PACKAGE: { working: "Paket bilgileri güncelleniyor…", success: "Paket bilgileri güncellendi." },
  CREATE_PAYMENT: { working: "Ödeme kaydediliyor…", success: "Ödeme kaydedildi." },
  VOID_PAYMENT: { working: "Ödeme kaydı kaldırılıyor…", success: "Ödeme kaydı kaldırıldı." },
  DELETE_PAYMENT: { working: "Ödeme işlemi geri alınıyor…", success: "Ödeme kaydı silindi." },
  CREATE_EXPENSE: { working: "Gider kaydediliyor…", success: "Gider kaydedildi." },
  VOID_EXPENSE: { working: "Gider kaydı kaldırılıyor…", success: "Gider kaydı kaldırıldı." },
  CREATE_OPPORTUNITY: { working: "Fırsat kartı oluşturuluyor…", success: "Fırsat kartı oluşturuldu." },
  UPDATE_OPPORTUNITY: { working: "Fırsat kartı güncelleniyor…", success: "Fırsat kartı güncellendi." },
  ARCHIVE_OPPORTUNITY: { working: "Fırsat kartı kapatılıyor…", success: "Fırsat kartı kapatıldı." },
  BURN_PACKAGE: { working: "Kalan haklar kapatılıyor…", success: "Kalan haklar kapatıldı." },
  UPDATE_PACKAGE_PLAN_PRICES: { working: "Fiyat kataloğu güncelleniyor…", success: "Fiyat kataloğu güncellendi." },
  UPDATE_PACKAGE_RULES: { working: "Paket kuralları güncelleniyor…", success: "Paket kuralları güncellendi." },
  UPDATE_WORKSPACE_SETTINGS: { working: "Portal ayarları güncelleniyor…", success: "Portal ayarları güncellendi." },
  CREATE_NOTE: { working: "Not ekip panosuna ekleniyor…", success: "Not ekip panosuna eklendi." },
  SET_NOTE_STATUS: { working: "Not durumu güncelleniyor…", success: "Not durumu güncellendi." },
  CREATE_TASK: { working: "Görev oluşturuluyor…", success: "Görev oluşturuldu." },
  SET_TASK_STATUS: { working: "Görev durumu güncelleniyor…", success: "Görev durumu güncellendi." },
  CREATE_GOAL: { working: "Hedef oluşturuluyor…", success: "Hedef oluşturuldu." },
  UPDATE_GOAL_PROGRESS: { working: "Hedef ilerlemesi güncelleniyor…", success: "Hedef ilerlemesi güncellendi." },
  ADD_GOAL_DAILY_RESULT: { working: "Hedef sonucu kaydediliyor…", success: "Hedef sonucu kaydedildi." },
  CREATE_SCHEDULE_EXCEPTIONS: { working: "İzin günleri programa ekleniyor…", success: "İzin günleri programa eklendi." },
  CANCEL_SCHEDULE_EXCEPTION: { working: "İzin kaydı kaldırılıyor…", success: "İzin kaydı kaldırıldı." }
};

function operationErrorCopy(error: unknown) {
  return studioErrorCopy(error);
}

export const defaultPackageRules: PackageRules = {
  eight: { validityDays: 45, alternativeValidityDays: 60, cancellationAllowance: 4 },
  sixteen: { validityDays: 90, alternativeValidityDays: 120, cancellationAllowance: 8 },
  manualSchrothFive: { validityDays: 30, alternativeValidityDays: 40 },
  manualSchrothTen: { validityDays: 60, alternativeValidityDays: 80 }
};

export const defaultWorkspaceSettings: WorkspaceSettings = {
  sessionReminders: true,
  cancellationConfirmations: true,
  compactView: false
};

export function defaultAppointmentRange(): AppointmentRange {
  return appointmentRangeAroundToday();
}

export function automaticRefreshAllowed(value = new Date()) {
  return istanbulHour(value) >= 7;
}

export function StudioProvider({ children }: { children: React.ReactNode }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [mobileActivities, setMobileActivities] = useState<MobileActivity[]>([]);
  const [packageRules, setPackageRules] = useState(defaultPackageRules);
  const [workspaceSettings, setWorkspaceSettings] = useState(defaultWorkspaceSettings);
  const [records, setRecords] = useState<PackageRecord[]>([]);
  const [packagePlans, setPackagePlans] = useState<PackagePlan[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<Array<{ code: string; name: string }>>([]);
  const [serviceTypes, setServiceTypes] = useState<Array<{ code: string; name: string; capacity: number }>>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [expenses, setExpenses] = useState<CostEntry[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [staffSchedules, setStaffSchedules] = useState<TrainerSchedule[]>([]);
  const [scheduleExceptions, setScheduleExceptions] = useState<TrainerException[]>([]);
  const [teamNotes, setTeamNotes] = useState<TeamNote[]>([]);
  const [workTasks, setWorkTasks] = useState<WorkTask[]>([]);
  const [staffGoals, setStaffGoals] = useState<StaffGoal[]>([]);
  const [goalDailyEntries, setGoalDailyEntries] = useState<GoalDailyEntry[]>([]);
  const [reversalLogs, setReversalLogs] = useState<ReversalLog[]>([]);
  const [studioClosureLogs, setStudioClosureLogs] = useState<StudioClosureLog[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [workshopManagementReady, setWorkshopManagementReady] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>("loading");
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [appointmentRange, setAppointmentRange] = useState<AppointmentRange>(
    defaultAppointmentRange
  );
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [operationState, setOperationState] = useState<OperationState | null>(null);
  const mutationQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const pendingMutationsRef = useRef(new Map<string, Promise<unknown>>());
  const mutationIdempotencyRef = useRef(new Map<string, string>());
  const operationTimerRef = useRef<number | null>(null);
  const pulseRequestRef = useRef<Promise<void> | null>(null);
  const lastPulseAtRef = useRef(0);
  const lastFullRefreshAtRef = useRef(0);

  const showOperationState = useCallback((next: OperationState, duration?: number) => {
    if (operationTimerRef.current !== null) window.clearTimeout(operationTimerRef.current);
    setOperationState(next);
    if (duration) {
      operationTimerRef.current = window.setTimeout(() => {
        setOperationState(null);
        operationTimerRef.current = null;
      }, duration);
    }
  }, []);

  useEffect(() => () => {
    if (operationTimerRef.current !== null) window.clearTimeout(operationTimerRef.current);
  }, []);

  const refreshPulse = useCallback(() => {
    if (pulseRequestRef.current) return pulseRequestRef.current;

    const request = studioApi
      .pulse(appointmentRange)
      .then((snapshot) => {
        setAppointments(snapshot.appointments);
        setMobileActivities(snapshot.mobileActivities ?? []);
        setLastSyncedAt(snapshot.generatedAt);
        lastPulseAtRef.current = Date.now();
      })
      .finally(() => {
        if (pulseRequestRef.current === request) pulseRequestRef.current = null;
      });
    pulseRequestRef.current = request;
    return request;
  }, [appointmentRange]);

  const refresh = useCallback(async () => {
    try {
      const snapshot = await studioApi.bootstrap(appointmentRange);
      setCustomers(snapshot.customers);
      setAppointments(snapshot.appointments);
      setRecords(snapshot.records);
      setPackagePlans(snapshot.packagePlans ?? []);
      setPaymentMethods(snapshot.paymentMethods ?? []);
      setServiceTypes(snapshot.serviceTypes ?? []);
      setPayments(snapshot.payments);
      setExpenses(snapshot.expenses ?? []);
      setOpportunities(snapshot.opportunities ?? []);
      setPackageRules(snapshot.packageRules);
      setWorkspaceSettings(snapshot.workspaceSettings ?? defaultWorkspaceSettings);
      setStaff(snapshot.staff ?? []);
      setStaffSchedules(snapshot.staffSchedules ?? []);
      setScheduleExceptions(snapshot.scheduleExceptions ?? []);
      setTeamNotes(snapshot.teamNotes ?? []);
      setWorkTasks(snapshot.workTasks ?? []);
      setStaffGoals(snapshot.staffGoals ?? []);
      setGoalDailyEntries(snapshot.goalDailyEntries ?? []);
      setReversalLogs(snapshot.reversalLogs ?? []);
      setStudioClosureLogs(snapshot.studioClosureLogs ?? []);
      setWorkshops(snapshot.workshops ?? []);
      setWorkshopManagementReady(snapshot.workshopManagementReady ?? false);
      setLastSyncedAt(snapshot.generatedAt);
      lastFullRefreshAtRef.current = Date.now();
      setConnectionError(null);
      setConnectionState("live");
      void studioApi
        .bootstrap(appointmentRange, undefined, true)
        .then((ledgerSnapshot) => setLedger(ledgerSnapshot.ledger))
        .catch(() => undefined);
      void refreshPulse().catch(() => undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Bağlantı kurulamadı";
      setConnectionError(message);
      setConnectionState("error");
    }
  }, [appointmentRange, refreshPulse]);

  const loadAppointmentRange = useCallback(async (range: AppointmentRange) => {
    setAppointmentsLoading(true);
    try {
      const snapshot = await studioApi.bootstrap(range);
      setCustomers(snapshot.customers);
      setAppointments(snapshot.appointments);
      setRecords(snapshot.records);
      setPackagePlans(snapshot.packagePlans ?? []);
      setPaymentMethods(snapshot.paymentMethods ?? []);
      setServiceTypes(snapshot.serviceTypes ?? []);
      setPayments(snapshot.payments);
      setExpenses(snapshot.expenses ?? []);
      setOpportunities(snapshot.opportunities ?? []);
      setPackageRules(snapshot.packageRules);
      setWorkspaceSettings(snapshot.workspaceSettings ?? defaultWorkspaceSettings);
      setStaff(snapshot.staff ?? []);
      setStaffSchedules(snapshot.staffSchedules ?? []);
      setScheduleExceptions(snapshot.scheduleExceptions ?? []);
      setTeamNotes(snapshot.teamNotes ?? []);
      setWorkTasks(snapshot.workTasks ?? []);
      setStaffGoals(snapshot.staffGoals ?? []);
      setGoalDailyEntries(snapshot.goalDailyEntries ?? []);
      setReversalLogs(snapshot.reversalLogs ?? []);
      setStudioClosureLogs(snapshot.studioClosureLogs ?? []);
      setWorkshops(snapshot.workshops ?? []);
      setWorkshopManagementReady(snapshot.workshopManagementReady ?? false);
      setAppointmentRange(range);
      setLastSyncedAt(snapshot.generatedAt);
      lastFullRefreshAtRef.current = Date.now();
      setConnectionError(null);
      setConnectionState("live");
    } catch (error) {
      setConnectionError(error instanceof Error ? error.message : "Randevular yüklenemedi");
      setConnectionState("error");
      throw error;
    } finally {
      setAppointmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    studioApi
      .bootstrap(appointmentRange, controller.signal)
      .then((snapshot) => {
        setCustomers(snapshot.customers);
        setAppointments(snapshot.appointments);
        setRecords(snapshot.records);
        setPackagePlans(snapshot.packagePlans ?? []);
        setPaymentMethods(snapshot.paymentMethods ?? []);
        setServiceTypes(snapshot.serviceTypes ?? []);
        setPayments(snapshot.payments);
        setExpenses(snapshot.expenses ?? []);
        setOpportunities(snapshot.opportunities ?? []);
        setLedger(snapshot.ledger);
        setPackageRules(snapshot.packageRules);
        setWorkspaceSettings(snapshot.workspaceSettings ?? defaultWorkspaceSettings);
        setStaff(snapshot.staff ?? []);
        setStaffSchedules(snapshot.staffSchedules ?? []);
        setScheduleExceptions(snapshot.scheduleExceptions ?? []);
        setTeamNotes(snapshot.teamNotes ?? []);
        setWorkTasks(snapshot.workTasks ?? []);
        setStaffGoals(snapshot.staffGoals ?? []);
        setGoalDailyEntries(snapshot.goalDailyEntries ?? []);
        setReversalLogs(snapshot.reversalLogs ?? []);
        setStudioClosureLogs(snapshot.studioClosureLogs ?? []);
        setWorkshops(snapshot.workshops ?? []);
        setWorkshopManagementReady(snapshot.workshopManagementReady ?? false);
        setLastSyncedAt(snapshot.generatedAt);
        lastFullRefreshAtRef.current = Date.now();
        setConnectionError(null);
        setConnectionState("live");
        void studioApi
          .bootstrap(appointmentRange, controller.signal, true)
          .then((ledgerSnapshot) => {
            if (!controller.signal.aborted) setLedger(ledgerSnapshot.ledger);
          })
          .catch(() => undefined);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setConnectionError(error instanceof Error ? error.message : "Bağlantı kurulamadı");
        setConnectionState("error");
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const pulseInterval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (!automaticRefreshAllowed()) return;
      void refreshPulse().catch(() => undefined);
    }, 15 * 60 * 1000);

    const refreshWhenActive = () => {
      if (document.visibilityState !== "visible") return;
      if (!automaticRefreshAllowed()) return;
      const now = Date.now();
      if (now - lastFullRefreshAtRef.current >= 15 * 60 * 1000) {
        void refresh();
        return;
      }
      if (now - lastPulseAtRef.current < 15 * 60 * 1000) return;
      void refreshPulse().catch(() => undefined);
    };
    if (automaticRefreshAllowed()) {
      void refreshPulse().catch(() => undefined);
    }
    window.addEventListener("focus", refreshWhenActive);
    window.addEventListener("online", refreshWhenActive);
    document.addEventListener("visibilitychange", refreshWhenActive);
    return () => {
      window.clearInterval(pulseInterval);
      window.removeEventListener("focus", refreshWhenActive);
      window.removeEventListener("online", refreshWhenActive);
      document.removeEventListener("visibilitychange", refreshWhenActive);
    };
  }, [refresh, refreshPulse]);

  const runAndRefresh = useCallback(
    async (action: StudioCommand, payload: unknown, financePin?: string) => {
      const mutationKey = `${action}:${JSON.stringify(payload)}`;
      const existingRequest = pendingMutationsRef.current.get(mutationKey);
      if (existingRequest) return existingRequest;
      const idempotencyKey =
        mutationIdempotencyRef.current.get(mutationKey) ?? crypto.randomUUID();
      mutationIdempotencyRef.current.set(mutationKey, idempotencyKey);

      const copy = operationCopy[action] ?? {
        working: "İşleminiz gerçekleştiriliyor…",
        success: "İşlem tamamlandı."
      };

      const request = mutationQueueRef.current.catch(() => undefined).then(async () => {
        showOperationState({ phase: "working", message: copy.working });
        const response = await studioApi.command(action, payload, idempotencyKey, financePin);
        await refresh();
        return response.result;
      });
      pendingMutationsRef.current.set(mutationKey, request);
      mutationQueueRef.current = request;

      try {
        const result = await request;
        mutationIdempotencyRef.current.delete(mutationKey);
        showOperationState({ phase: "success", message: copy.success }, 1600);
        return result;
      } catch (error) {
        const refreshRequired =
          error instanceof StudioApiError &&
          /CONCURRENCY|VERSION_CONFLICT/.test(
            `${error.code} ${error.message}`.toUpperCase()
          );
        if (refreshRequired) {
          await refresh().catch(() => undefined);
        }
        showOperationState(
          { phase: "error", message: operationErrorCopy(error) },
          3600
        );
        throw error;
      } finally {
        pendingMutationsRef.current.delete(mutationKey);
      }
    },
    [refresh, showOperationState]
  );

  async function createCustomer(customer: Customer) {
    const result = await runAndRefresh("CREATE_CUSTOMER", {
      fullName: customer.name,
      phone: customer.phone,
      email: customer.email,
      gender: customer.gender,
      birthDate: customer.birthDate,
      district: customer.district,
      notes: customer.notes,
      healthHistory: customer.history,
      registeredOn: customer.registrationDate,
      allowSms: !customer.dontSendSms,
      requiresExclusiveSlot: customer.requiresExclusiveSlot ?? false,
      avoidsMaleCoAttendee: customer.avoidsMaleCoAttendee ?? false,
      experienceLevel: customer.experienceLevel ?? "INTERMEDIATE"
    }) as { responseJson?: string };
    const response = result?.responseJson ? JSON.parse(result.responseJson) : null;
    const customerId = String(response?.entityPublicId ?? "");
    if (!customerId) throw new Error("CUSTOMER_CREATE_RESPONSE_INVALID");

    if (!customer.email?.trim()) return { customerId };

    try {
      const mobileResponse = await studioApi.command<CustomerAppCredential>(
        "CREATE_MOBILE_ACCOUNT",
        { customerId }
      );
      const mobileCredential = mobileResponse.result;
      await refresh().catch(() => undefined);
      return { customerId, mobileCredential };
    } catch (error) {
      return {
        customerId,
        mobileAccountError: studioErrorCopy(
          error,
          "Müşteri oluşturuldu ancak mobil uygulama erişimi hazırlanamadı. App Bilgileri bölümünden yeniden deneyin."
        )
      };
    }
  }

  async function updateCustomer(customer: Customer) {
    await runAndRefresh("UPDATE_CUSTOMER", {
      customerId: customer.id,
      version: customer.version,
      fullName: customer.name,
      phone: customer.phone,
      email: customer.email,
      gender: customer.gender,
      birthDate: customer.birthDate,
      district: customer.district,
      notes: customer.notes,
      healthHistory: customer.history,
      isActive: customer.status === "active",
      allowSms: !customer.dontSendSms,
      requiresExclusiveSlot: customer.requiresExclusiveSlot ?? false,
      avoidsMaleCoAttendee: customer.avoidsMaleCoAttendee ?? false,
      experienceLevel: customer.experienceLevel ?? "INTERMEDIATE"
    });
  }

  async function createAppointments(payload: {
    customerId: string;
    recordId: string;
    date: string;
    time: string;
    trainer: string;
    note?: string;
    repeatCount?: number;
  }) {
    await runAndRefresh("CREATE_APPOINTMENTS", payload);
  }

  async function setAppointmentStatus(
    appointment: Appointment,
    status: AppointmentStatus,
    sessionNotes?: string,
    completedBy?: string,
    cancellationMode: CancellationMode = "customer",
    cancellationReason?: string,
    extendExpiryDays?: number
  ) {
    await runAndRefresh(
      status === "cancelled" && cancellationMode === "studio"
        ? "CANCEL_APPOINTMENT_PROTECTED"
        : status === "planned" && appointment.status !== "planned"
        ? "REOPEN_APPOINTMENT"
        : "SET_APPOINTMENT_STATUS",
      {
      appointmentId: appointment.id,
      version: appointment.version,
      status,
      sessionNotes,
      completedBy,
      cancellationMode,
      cancellationReason,
      extendExpiryDays
      }
    );
  }

  async function closeStudioDay(payload: { date: string; reason?: string }) {
    await runAndRefresh("CLOSE_STUDIO_DAY", payload);
  }

  async function extendStudioClosurePackages(payload: { date: string; days: number }) {
    await runAndRefresh("EXTEND_STUDIO_CLOSURE_PACKAGES", payload);
  }

  async function voidAppointment(appointment: Appointment, removalNote?: string) {
    await runAndRefresh("VOID_APPOINTMENT", {
      appointmentId: appointment.id,
      version: appointment.version,
      removalNote
    });
  }

  async function createHold(payload: {
    customerId: string;
    date: string;
    time: string;
    trainer?: string;
    note?: string;
  }) {
    await runAndRefresh("CREATE_HOLD", payload);
  }

  async function confirmHold(
    hold: Appointment,
    packageId: string,
    completeNow: boolean,
    sessionNotes?: string,
    completedBy?: string
  ) {
    await runAndRefresh("CONFIRM_HOLD", {
      holdId: hold.id,
      version: hold.version,
      packageId,
      completeNow,
      sessionNotes,
      completedBy
    });
  }

  async function cancelHold(hold: Appointment) {
    await runAndRefresh("CANCEL_HOLD", {
      holdId: hold.id,
      version: hold.version
    });
  }

  async function deleteHold(hold: Appointment) {
    await runAndRefresh("DELETE_HOLD", {
      holdId: hold.id,
      version: hold.version
    });
  }

  async function addRecord(record: PackageRecord) {
    await runAndRefresh("CREATE_PACKAGE", {
      customerId: record.customerId,
      packageName: record.detail,
      purchasedSessions: record.quantity,
      totalAmount: record.price,
      purchasedOn: record.registrationDate,
      expiresOn: record.expiryDate,
      cancellationAllowance: record.cancellationAllowance,
      packagePlanCode: record.packagePlanCode,
      serviceTypeCode: record.serviceTypeCode,
      pricingMode: record.pricingMode,
      listAmount: record.listAmount,
      discountType: record.discountType,
      discountPercent: record.discountPercent,
      expectedPaymentMethodCode: record.expectedPaymentMethodCode,
      vatApplied: record.vatApplied,
      vatRate: record.vatRate,
      vatAmount: record.vatAmount,
      netAmount: record.netAmount
    });
  }

  async function updateRecord(record: PackageRecord) {
    await runAndRefresh("UPDATE_PACKAGE", {
      packageId: record.id,
      version: record.version,
      packageName: record.detail,
      purchasedSessions: record.quantity,
      totalAmount: record.price,
      purchasedOn: record.registrationDate,
      expiresOn: record.expiryDate,
      cancellationAllowance: record.cancellationAllowance,
      packagePlanCode: record.packagePlanCode,
      serviceTypeCode: record.serviceTypeCode,
      pricingMode: record.pricingMode,
      listAmount: record.listAmount,
      discountType: record.discountType,
      discountPercent: record.discountPercent,
      expectedPaymentMethodCode: record.expectedPaymentMethodCode,
      vatApplied: record.vatApplied,
      vatRate: record.vatRate,
      vatAmount: record.vatAmount,
      netAmount: record.netAmount,
      finalPriceOverride: record.pricingMode === "FINAL_OVERRIDE"
    });
  }

  async function addPayment(payment: Payment) {
    await runAndRefresh("CREATE_PAYMENT", {
      customerId: payment.customerId,
      packageId: payment.recordId,
      amount: payment.amount,
      paidOn: payment.date,
      paymentMethod: payment.method
    });
  }

  async function removePayment(paymentId: string) {
    const payment = payments.find((item) => item.id === paymentId);
    if (!payment) return;
    await runAndRefresh("DELETE_PAYMENT", {
      paymentId: payment.id,
      version: payment.version
    });
  }

  async function createExpense(payload: {
    category: string;
    description: string;
    amount: number;
    date: string;
    note?: string;
  }) {
    await runAndRefresh("CREATE_EXPENSE", payload);
  }

  async function voidExpense(expense: CostEntry) {
    await runAndRefresh("VOID_EXPENSE", {
      expenseId: expense.id,
      version: expense.version
    });
  }

  async function createOpportunity(opportunity: Opportunity) {
    await runAndRefresh("CREATE_OPPORTUNITY", {
      contactName: opportunity.name,
      phone: opportunity.phone,
      request: opportunity.request,
      status: opportunity.status,
      ownerId: opportunity.ownerId,
      contactDate: opportunity.contactDate,
      serviceInterest: opportunity.type,
      communicationType: opportunity.communicationType,
      notes: opportunity.notes
    });
  }

  async function updateOpportunity(opportunity: Opportunity) {
    await runAndRefresh("UPDATE_OPPORTUNITY", {
      opportunityId: opportunity.id,
      version: opportunity.version,
      contactName: opportunity.name,
      phone: opportunity.phone,
      request: opportunity.request,
      status: opportunity.status,
      ownerId: opportunity.ownerId,
      contactDate: opportunity.contactDate,
      serviceInterest: opportunity.type,
      communicationType: opportunity.communicationType,
      notes: opportunity.notes
    });
  }

  async function archiveOpportunity(opportunity: Opportunity) {
    await runAndRefresh("ARCHIVE_OPPORTUNITY", {
      opportunityId: opportunity.id,
      version: opportunity.version
    });
  }

  function adjustCancellationUsage(recordId: string, difference: number) {
    setRecords((items) =>
      items.map((record) =>
        record.id === recordId
          ? {
              ...record,
              cancellationsUsed: Math.max(
                0,
                Math.min(
                  record.cancellationAllowance ?? 0,
                  (record.cancellationsUsed ?? 0) + difference
                )
              )
            }
          : record
      )
    );
  }

  async function burnRecordRights(recordId: string) {
    const record = records.find((item) => item.id === recordId);
    if (!record) return;
    await runAndRefresh("BURN_PACKAGE", {
      packageId: record.id,
      version: record.version,
      reason: "Paket süresi dolduğu için kalan haklar kapatıldı."
    });
  }

  async function savePackageRules(rules: PackageRules) {
    await runAndRefresh("UPDATE_PACKAGE_RULES", rules);
  }

  async function updatePackagePlanPrices(
    updates: Array<{ id: string; version: string; defaultPrice: number }>,
    financePin: string
  ) {
    await runAndRefresh("UPDATE_PACKAGE_PLAN_PRICES", { prices: updates }, financePin);
  }

  async function saveWorkspaceSettings(settings: WorkspaceSettings, rules: PackageRules) {
    await runAndRefresh("UPDATE_WORKSPACE_SETTINGS", {
      ...settings,
      packageRules: rules
    });
  }

  async function createTeamNote(payload: {
    body: string;
    priority: WorkPriority;
    dueOn?: string;
    assignedToId?: string;
    authorStaffId?: string;
  }) {
    await runAndRefresh("CREATE_NOTE", payload);
  }

  async function setTeamNoteStatus(note: TeamNote, status: "OPEN" | "DONE" | "ARCHIVED") {
    await runAndRefresh("SET_NOTE_STATUS", {
      noteId: note.id,
      version: note.version,
      status
    });
  }

  async function createWorkTask(payload: {
    title: string;
    description?: string;
    priority: WorkPriority;
    recurrence: WorkTask["recurrence"];
    dueOn: string;
    assignedToId: string;
    createdByStaffId?: string;
  }) {
    await runAndRefresh("CREATE_TASK", payload);
  }

  async function setWorkTaskStatus(task: WorkTask, status: WorkTask["status"] | "CANCELLED") {
    await runAndRefresh("SET_TASK_STATUS", {
      taskId: task.id,
      version: task.version,
      status
    });
  }

  async function createStaffGoal(payload: {
    title: string;
    metricLabel: string;
    metricCode: StaffGoal["metricCode"];
    ownerId: string;
    periodStart: string;
    periodEnd: string;
    priority: WorkPriority;
    startValue: number;
    currentValue: number;
    targetValue: number;
    rewardAmount: number;
    aggregation: StaffGoal["aggregation"];
    notes?: string;
  }) {
    await runAndRefresh("CREATE_GOAL", payload);
  }

  async function updateStaffGoalProgress(goal: StaffGoal, currentValue: number) {
    await runAndRefresh("UPDATE_GOAL_PROGRESS", {
      goalId: goal.id,
      version: goal.version,
      currentValue
    });
  }

  async function addGoalDailyResult(payload: {
    goalId: string;
    entryDate: string;
    value: number;
    recordedByStaffId?: string;
    note?: string;
  }) {
    await runAndRefresh("ADD_GOAL_DAILY_RESULT", payload);
  }

  async function createScheduleExceptions(payload: {
    staffId: string;
    dates: string[];
    startTime: string;
    endTime: string;
    allDay: boolean;
    note?: string;
  }) {
    await runAndRefresh("CREATE_SCHEDULE_EXCEPTIONS", payload);
  }

  async function cancelScheduleException(exception: TrainerException) {
    await runAndRefresh("CANCEL_SCHEDULE_EXCEPTION", {
      exceptionId: exception.id,
      version: exception.version
    });
  }

  async function createWorkshop(workshop: WorkshopInput) {
    await runAndRefresh("CREATE_WORKSHOP", workshop);
  }

  async function updateWorkshop(workshop: Workshop, input: WorkshopInput) {
    await runAndRefresh("UPDATE_WORKSHOP", {
      ...input,
      workshopId: workshop.id,
      version: workshop.version
    });
  }

  async function archiveWorkshop(workshop: Workshop) {
    await runAndRefresh("ARCHIVE_WORKSHOP", {
      workshopId: workshop.id,
      version: workshop.version
    });
  }

  async function cancelWorkshopParticipation(
    registration: WorkshopRegistration,
    reason: string
  ) {
    if (!registration.participationId || !registration.participationVersion) {
      throw new Error("WORKSHOP_PARTICIPATION_NOT_FOUND");
    }
    await runAndRefresh("CANCEL_WORKSHOP_PARTICIPATION", {
      participationId: registration.participationId,
      version: registration.participationVersion,
      reason: reason.trim()
    });
  }

  const value = useMemo(
    () => ({
      customers,
      setCustomers,
      appointments,
      setAppointments,
      mobileActivities,
      records,
      packagePlans,
      paymentMethods,
      serviceTypes,
      payments,
      expenses,
      opportunities,
      ledger,
      setLedger,
      packageRules,
      setPackageRules,
      workspaceSettings,
      staff,
      staffSchedules,
      scheduleExceptions,
      teamNotes,
      workTasks,
      staffGoals,
      goalDailyEntries,
      reversalLogs,
      studioClosureLogs,
      workshops,
      workshopManagementReady,
      connectionState,
      connectionError,
      lastSyncedAt,
      appointmentRange,
      appointmentsLoading,
      operationState,
      refresh,
      loadAppointmentRange,
      createCustomer,
      updateCustomer,
      createAppointments,
      setAppointmentStatus,
      closeStudioDay,
      extendStudioClosurePackages,
      voidAppointment,
      createHold,
      confirmHold,
      cancelHold,
      deleteHold,
      addRecord,
      updateRecord,
      addPayment,
      removePayment,
      createExpense,
      voidExpense,
      createOpportunity,
      updateOpportunity,
      archiveOpportunity,
      adjustCancellationUsage,
      burnRecordRights,
      updatePackagePlanPrices,
      savePackageRules,
      saveWorkspaceSettings,
      createTeamNote,
      setTeamNoteStatus,
      createWorkTask,
      setWorkTaskStatus,
      createStaffGoal,
      updateStaffGoalProgress,
      addGoalDailyResult,
      createScheduleExceptions,
      cancelScheduleException,
      createWorkshop,
      updateWorkshop,
      archiveWorkshop,
      cancelWorkshopParticipation
    }),
    [
      customers,
      appointments,
      mobileActivities,
      records,
      packagePlans,
      paymentMethods,
      serviceTypes,
      payments,
      expenses,
      opportunities,
      ledger,
      packageRules,
      workspaceSettings,
      staff,
      staffSchedules,
      scheduleExceptions,
      teamNotes,
      workTasks,
      staffGoals,
      goalDailyEntries,
      reversalLogs,
      studioClosureLogs,
      workshops,
      workshopManagementReady,
      connectionState,
      connectionError,
      lastSyncedAt,
      appointmentRange,
      appointmentsLoading,
      operationState,
      refresh
    ]
  );

  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>;
}

export function useStudio() {
  const value = useContext(StudioContext);
  if (!value) throw new Error("useStudio must be used inside StudioProvider");
  return value;
}
